import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Assessment,
  FiberManualRecord,
} from '@mui/icons-material';

import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine,
} from 'recharts';
import { Trade, PortfolioSummary } from '../../types';
import { useTradeStream, TradeEvent } from '../../hooks/useTradeStream';
import { useLiveAccount } from '../../hooks/useLiveAccount';

const Dashboard: React.FC = () => {
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [cycleHistory, setCycleHistory] = useState<Array<{
    date: string; closedAt: string; symbol: string;
    realizedPnL: number; cumulativePnL: number;
  }>>([]);
  const [pnlRange, setPnlRange] = useState<'1D' | '5D' | '30D' | '1Y' | '5Y'>('30D');
  const [realizedSummary, setRealizedSummary] = useState<{
    today: number; week: number; allTime: number;
    cycleCount: number; winCount: number;
  } | null>(null);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCycleHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/realized-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCycleHistory(data.cycles || []);
        setRealizedSummary(data.summary || null);
      }
    } catch (err) {
      console.error('Cycle history fetch error:', err);
    }
  };

  useEffect(() => {
    fetchCycleHistory();
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    await fetchDashboardDataImpl(true);
  };

  // Shared fetch helper. `showSpinner=false` lets live-update refreshes
  // update the DOM without the full-page loading flash.
  const fetchDashboardDataImpl = async (showSpinner: boolean) => {
    try {
      if (showSpinner) setLoading(true);
      setError('');

      // Fetch portfolio and trades in parallel
      const [portfolioResponse, tradesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/portfolio`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }),
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        })
      ]);

      if (portfolioResponse.ok && tradesResponse.ok) {
        const portfolioData = await portfolioResponse.json();
        const tradesData = await tradesResponse.json();

        setPortfolioSummary(portfolioData.portfolio.summary);
        setRecentTrades(tradesData.trades.slice(0, 5)); // Show last 5 trades
      } else if (showSpinner) {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      if (showSpinner) setError('Network error. Please try again.');
      console.error('Dashboard fetch error:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // Live stream: any fill / terminal / state change reshapes the
  // portfolio (premium collected, shares, bot state). Refresh quietly.
  const handleTradeEvent = useCallback((_ev: TradeEvent) => {
    fetchDashboardDataImpl(false);
  }, []);
  const { status: streamStatus } = useTradeStream(handleTradeEvent);

  // Live Alpaca account snapshot — seeds from REST on mount, then
  // updates inline from account_synced WS events (no dashboard refetch).
  const { account } = useLiveAccount();

  const _fmt = (v: number | null | undefined) =>
    v === null || v === undefined
      ? '—'
      : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const PNL_RANGE_DAYS: Record<'1D' | '5D' | '30D' | '1Y' | '5Y', number> = {
    '1D': 1, '5D': 5, '30D': 30, '1Y': 365, '5Y': 1825,
  };
  // Window the cycle history and re-baseline the cumulative line to zero at
  // the window start, so each range answers "how did THIS period go."
  const rangedHistory = React.useMemo(() => {
    // 1D/5D are SESSION windows (finance convention): the last N distinct
    // trading dates present in the data — so a weekend "5D" shows Mon-Fri
    // and "1D" shows the most recent session, never an empty "today".
    // 30D+ are calendar windows from midnight.
    let inWindow;
    if (pnlRange === '1D' || pnlRange === '5D') {
      const dates = Array.from(new Set(cycleHistory.map(c => c.date)));
      const keep = new Set(dates.slice(-PNL_RANGE_DAYS[pnlRange]));
      inWindow = cycleHistory.filter(c => keep.has(c.date));
    } else {
      const midnight = new Date();
      midnight.setHours(0, 0, 0, 0);
      const cutoff = midnight.getTime() - (PNL_RANGE_DAYS[pnlRange] - 1) * 24 * 60 * 60 * 1000;
      inWindow = cycleHistory.filter(c => new Date(c.closedAt).getTime() >= cutoff);
    }
    let running = 0;
    return inWindow.map(c => {
      running = Math.round((running + c.realizedPnL) * 100) / 100;
      return { ...c, cumulativePnL: running, ts: new Date(c.closedAt).getTime() };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleHistory, pnlRange]);

  // SESSION axis (what real trading charts use): each trading day is one
  // equal unit; cycles position within their day by time-of-day fraction
  // (9:30-16:00 ET); weekends and skip days don't exist on the axis. This
  // gives even tick spacing AND no dead weekend stretches AND intraday
  // clustering, which no linear-time or per-cycle axis can do at once.
  const sessionAxis = React.useMemo(() => {
    const sessions: string[] = [];
    const seen = new Set<string>();
    for (const c of rangedHistory) {
      if (!seen.has(c.date)) { seen.add(c.date); sessions.push(c.date); }
    }
    const idx = new Map(sessions.map((d, i) => [d, i]));
    const frac = (iso: string) => {
      const d = new Date(iso);
      const mins = d.getHours() * 60 + d.getMinutes();
      return Math.min(1, Math.max(0, (mins - 570) / 390)); // 9:30 -> 16:00
    };
    const data = rangedHistory.map(c => ({
      ...c,
      x: (idx.get(c.date) ?? 0) + frac(c.closedAt),
    }));
    let ticks: number[] | undefined;
    if (pnlRange === '1D' || sessions.length <= 1) {
      ticks = undefined; // intra-day: let recharts pick fractions, label as times
    } else {
      const step = Math.max(1, Math.ceil(sessions.length / 6));
      ticks = [];
      for (let i2 = 0; i2 < sessions.length; i2 += step) ticks.push(i2);
    }
    const fmt = (v: number) => {
      if (pnlRange === '1D' || sessions.length <= 1) {
        const base = 570 + Math.round((v - Math.floor(v)) * 390);
        const h = Math.floor(base / 60), m = base % 60;
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = ((h + 11) % 12) + 1;
        return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      const d = sessions[Math.round(v)];
      return d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    };
    return { data, sessions, ticks, fmt, domain: [0, Math.max(sessions.length, 1)] as [number, number] };
  }, [rangedHistory, pnlRange]);

  const stats = portfolioSummary ? [
    {
      title: 'Total Portfolio Value',
      value: `$${portfolioSummary.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: portfolioSummary.totalValue > 0 ? 
        `${((portfolioSummary.totalPnL / portfolioSummary.totalValue) * 100).toFixed(2)}%` : '0.00%',
      changeType: portfolioSummary.totalPnL >= 0 ? 'positive' : 'negative',
      icon: <AccountBalance sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Realized P&L (all-time)',
      value: `$${(realizedSummary?.allTime ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      change: `Unrealized on open positions: $${portfolioSummary.totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      changeType: (realizedSummary?.allTime ?? 0) >= 0 ? 'positive' : 'negative',
      icon: (realizedSummary?.allTime ?? 0) >= 0 ?
        <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} /> :
        <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />,
    },
    {
      title: 'Total Holdings',
      value: portfolioSummary.holdingCount.toString(),
      change: '',
      changeType: 'neutral' as const,
      icon: <Assessment sx={{ fontSize: 40, color: 'info.main' }} />,
    },
    {
      title: 'Recent Trades',
      value: recentTrades.length.toString(),
      change: '',
      changeType: 'neutral' as const,
      icon: <TrendingDown sx={{ fontSize: 40, color: 'warning.main' }} />,
    },
  ] : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'EXECUTED':
      case 'FILLED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActionColor = (action: string) => {
    return action.includes('BUY') ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Dashboard
        </Typography>
        <Tooltip
          title={
            streamStatus === 'open'
              ? 'Live — portfolio updates automatically from Alpaca'
              : streamStatus === 'connecting'
              ? 'Connecting to live updates…'
              : 'Live updates offline — data may be stale'
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FiberManualRecord
              sx={{
                fontSize: 12,
                color:
                  streamStatus === 'open'
                    ? 'success.main'
                    : streamStatus === 'connecting'
                    ? 'warning.main'
                    : 'text.disabled',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {streamStatus === 'open' ? 'Live' : streamStatus}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Live Alpaca account snapshot — updates in place from WS */}
      {account && (
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Cash</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{_fmt(account.cash)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Buying Power</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{_fmt(account.buyingPower)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Equity</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{_fmt(account.equity)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Options BP</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{_fmt(account.optionsBuyingPower)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">DT BP</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{_fmt(account.daytradingBuyingPower)}</Typography>
          </Box>
          {account.multiplier != null && account.multiplier > 1 && (
            <Chip label={`margin ${account.multiplier}x`} color="warning" size="small" />
          )}
        </Paper>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ ml: 2, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {stat.value}
                    </Typography>
                    {stat.change && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {stat.changeType === 'positive' && <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />}
                        {stat.changeType === 'negative' && <TrendingDown sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: stat.changeType === 'positive' ? 'success.main' : 
                                  stat.changeType === 'negative' ? 'error.main' : 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          {stat.change}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Portfolio Overview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Portfolio Overview
              </Typography>
              {portfolioSummary ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Value:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${portfolioSummary.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Unrealized P&L (open positions):</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      color={portfolioSummary.totalPnL >= 0 ? 'success.main' : 'error.main'}
                    >
                      ${portfolioSummary.totalPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Realized P&L (all-time):</Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={(realizedSummary?.allTime ?? 0) >= 0 ? 'success.main' : 'error.main'}
                    >
                      ${(realizedSummary?.allTime ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Holdings:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {portfolioSummary.holdingCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Return:</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      color={portfolioSummary.totalPnL >= 0 ? 'success.main' : 'error.main'}
                    >
                      {portfolioSummary.totalValue > 0 ? 
                        ((portfolioSummary.totalPnL / portfolioSummary.totalValue) * 100).toFixed(2) + '%' : 
                        '0.00%'
                      }
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No portfolio data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Trades */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Recent Trades
              </Typography>
              
              {recentTrades.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent trades found
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentTrades.map((trade) => (
                        <TableRow key={trade.id}>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {trade.symbol}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={trade.action} 
                              size="small"
                              color={getActionColor(trade.action)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {trade.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              ${trade.executedPrice ? Number(trade.executedPrice).toFixed(2) : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={trade.status} 
                              size="small"
                              color={getStatusColor(trade.status)}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Chart Placeholder */}
      <Grid size={{ xs: 12 }}>
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Cumulative Realized P&L
              </Typography>
              <Stack direction="row" spacing={1}>
                {(['1D', '5D', '30D', '1Y', '5Y'] as const).map(r => (
                  <Chip
                    key={r}
                    label={r}
                    size="small"
                    color={pnlRange === r ? 'primary' : 'default'}
                    variant={pnlRange === r ? 'filled' : 'outlined'}
                    onClick={() => setPnlRange(r)}
                  />
                ))}
              </Stack>
            </Box>
            {rangedHistory.length === 0 ? (
              <Box sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.50',
                borderRadius: 2
              }}>
                <Typography variant="body1" color="text.secondary">
                  {cycleHistory.length === 0
                    ? 'No closed cycles yet — the realized P&L curve appears after the first cycle completes'
                    : `No closed cycles in this range (${pnlRange})`}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sessionAxis.data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                    <XAxis
                      dataKey="x"
                      type="number"
                      domain={sessionAxis.domain}
                      ticks={sessionAxis.ticks}
                      tickFormatter={sessionAxis.fmt}
                      tick={{ fontSize: 12, fill: '#757575' }}
                      tickLine={false}
                      minTickGap={40}
                    />
                    <YAxis
                      tickFormatter={(v: number) => `$${v}`}
                      tick={{ fontSize: 12, fill: '#757575' }}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                    />
                    <ReferenceLine y={0} stroke="#9e9e9e" strokeWidth={1} />
                    <RechartsTooltip
                      formatter={(value: any, name: any) => [
                        `$${Number(value).toFixed(2)}`,
                        name === 'cumulativePnL' ? 'Cumulative realized' : name,
                      ]}
                      labelFormatter={(_v: any, payload: any) => {
                        const p = payload && payload[0] && payload[0].payload;
                        if (!p) return '';
                        const when = new Date(p.closedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
                        return `${when} — ${p.symbol} cycle ${p.realizedPnL >= 0 ? '+' : ''}$${p.realizedPnL.toFixed(2)}`;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cumulativePnL"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Box>
  );
};

export default Dashboard;